import type { SnipcatPatch, SnipcatValues } from "./types";

/**
 * What may go into a funnel session, enforced on the client too.
 *
 * The server validates every `values` item as a scalar and rejects the WHOLE
 * patch when one of them is not, so a single array answer would silently kill
 * the tracking of an entire visit. Arrays of scalars are therefore joined
 * rather than dropped, and anything else is dropped rather than sent.
 */

/**
 * Keys carrying contact details. A funnel session is ANONYMOUS telemetry that
 * is readable over the API and outlives the erasure of the person, so the
 * visitor's e-mail/phone/name belongs on the LEAD they submit explicitly -
 * never in the session. The server drops these as well; doing it here too
 * means they never leave the browser in the first place.
 */
const IDENTIFYING_KEYS = [
  "email",
  "emailaddress",
  "mail",
  "phone",
  "phonenumber",
  "tel",
  "telephone",
  "mobile",
  "name",
  "firstname",
  "lastname",
  "fullname",
  "surname",
  "username",
  "contact",
  "address",
  "street",
  "message",
  "note",
  "password",
];

const normalizeKey = (key: string): string =>
  key
    .trim()
    .toLowerCase()
    .replace(/[-_\s]/g, "");

export const isIdentifyingKey = (key: string): boolean => {
  const normalized = normalizeKey(key);
  return IDENTIFYING_KEYS.some((item) => normalized === item);
};

/** Longest key and value the server accepts; longer ones are dropped/cut here. */
const MAX_KEY_LENGTH = 64;
const MAX_VALUE_LENGTH = 500;

const toScalar = (value: unknown): string | number | null => {
  if (typeof value === "string") {
    return value.length > MAX_VALUE_LENGTH
      ? value.slice(0, MAX_VALUE_LENGTH)
      : value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "boolean") {
    return String(value);
  }
  if (
    Array.isArray(value) &&
    value.every((item) => typeof item === "string" || typeof item === "number")
  ) {
    return toScalar(value.join(","));
  }
  return null;
};

/**
 * Scalars only, keys bounded, and - when `dropIdentifying` - without anything
 * that looks like a contact detail.
 */
export const sanitizeValues = (
  values: Record<string, unknown>,
  dropIdentifying = true,
): SnipcatValues => {
  const result: SnipcatValues = {};
  Object.keys(values || {}).forEach((rawKey) => {
    const key = rawKey.trim();
    if (!key || key.length > MAX_KEY_LENGTH) {
      return;
    }
    if (dropIdentifying && isIdentifyingKey(key)) {
      return;
    }
    const scalar = toScalar(values[rawKey]);
    if (scalar !== null) {
      result[key] = scalar;
    }
  });
  return result;
};

/**
 * `values` holds flat keys only, so the variant of an event (step id, clicked
 * target, …) becomes part of the KEY instead of a nested payload.
 */
export const eventKey = (event: string, variant?: string | null): string =>
  variant ? event + "_" + variant : event;

/** Collapses queued patches into a single request body. */
export const mergePatches = (patches: SnipcatPatch[]): SnipcatPatch => {
  const values: SnipcatValues = {};
  const pageStats: SnipcatPatch["pageStats"] = {};
  patches.forEach((patch) => {
    Object.assign(values, patch.values || {});
    Object.assign(pageStats, patch.pageStats || {});
  });
  return {
    ...(Object.keys(values).length ? { values } : {}),
    ...(Object.keys(pageStats).length ? { pageStats } : {}),
  };
};
